import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  filterMenuItems,
  isClubManager,
  hasAnyPermission,
  hasModuleAccess,
  User,
} from "../utils/roleBasedAccess";

// Function to get tournaments menu (same structure for all roles).
// Fine-grained visibility of items is controlled later via hasModuleAccess/filterMenuItems.
const getTournamentMenu = (userData: User) => {
    return {
        id: "tournaments",
        label: "Tournaments",
        icon: "ph-trophy",
        link: "/tournaments",
        subItems: [
            {
                id: "tournament-registrations",
                label: "Tournament Registrations",
                icon: "ph-list-checks",
                link: "/tournament-registrations",
                subItems: [
                    {
                        id: "tournament-registrations-dashboard",
                        label: "Dashboard",
                        icon: "ph-house",
                        // Use the global dashboard route; RoleBasedDashboard
                        // will redirect to the first available page based on
                        // permissions (including tournament.registrations.*).
                        link: "/dashboard",
                    },
                    {
                        id: "manage-registration-details",
                        label: "Manage Registration Details",
                        icon: "ph-file-text",
                        link: "/registered-tournaments",
                    },
                    { id: "allclubs", label: "All Clubs", icon: "ph-users-three", link: "/all-clubs" },
                    { id: "manageplayers", label: "Manage Players", icon: "ph-user", link: "/manage-players" },
                    { id: "superadminpayments", label: "Payments & Transactions", icon: "ph-credit-card", link: "/super-admin-payments" },
                    { id: "managetournaments", label: "Tournament Listings", icon: "ph-list", link: "/tournaments-listing" }
                ]
            },
            {
                id: "tournament-management",
                label: "Tournament Management",
                icon: "ph-calendar-check",
                link: null,
                subItems: [
                    { id: "managetournamentsfixtures", label: "Manage Tournaments", icon: "ph-calendar-check", link: "/manage-tournaments-fixtures" },
                    { id: "sportsmanagement", label: "Sports Management", icon: "ph-basketball", link: "/sports-management" }
                ]
            }
        ]
    };
};

const Navdata = () => {
    //state data
    const [isDashboard, setIsDashboard] = useState(false);
    const [isEcommerce, setIsEcommerce] = useState(false);
    const [isLearning, setIsLearning] = useState(false);
    const [isCourses, setIsCourses] = useState(false);
    const [isStudents, setIsStudents] = useState(false);
    const [isInstructors, setIsInstructors] = useState(false);
    const [isSupportTicket, setIsSupportTicket] = useState(false);
    const [isRealEstate, setIsRealEstate] = useState(false);
    const [isAuthentication, setIsAuthentication] = useState(false);
    const [isPages, setIsPages] = useState(false);
    const [isagent, setIsAgent] = useState(false);
    const [isagencies, setIsAgencies] = useState(false);
    const [isMyClub, setIsMyClub] = useState(false);

    // Components
    const [isBootstrapUi, setIsBootstrapUi] = useState(false);
    const [isAdvanceUi, setIsAdvanceUi] = useState(false);
    const [isCustomUi, setIsCustomUi] = useState(false);
    const [isWidgets, setIsWidgets] = useState(false);
    const [isForms, setIsForms] = useState(false);
    const [isTables, setIsTables] = useState(false);
    const [isCharts, setIsCharts] = useState(false);
    const [isIcons, setIsIcons] = useState(false);
    const [isMaps, setIsMaps] = useState(false);

    const [isOrder, setIsOrder] = useState(false);
    const [isInvoice, setIsInvoice] = useState(false);
    const [isShipping, setIsShipping] = useState(false);
    const [isLocalization, setIsLocalization] = useState(false);
    const [isAuth, setIsAuth] = useState(false);
    const [isMultiLevel, setIsMultiLevel] = useState(false);

    // Authentication
    const [isError, setIsError] = useState(false);

    // Multi Level
    const [isLevel1, setIsLevel1] = useState(false);
    const [isLevel2, setIsLevel2] = useState(false);

    const [iscurrentState, setIscurrentState] = useState('Dashboard');

    // Get user from Redux state
    const { user, role, permissions } = useSelector((state: any) => state.Login);

    const userData: User = {
        ...(user || {}),
        subRole: (user && user.subRole) || role,
        permissions: permissions,
    };

    // Detect when permission codes are loaded into Redux
    const rawPermissions: any = permissions;
    const permissionsLoaded =
        rawPermissions && Array.isArray(rawPermissions.permissionCodes);

    // While permissions are still loading after hydration, we optimistically
    // show all optional modules so the current page's menu item exists and can
    // be highlighted on refresh. Once permissions are loaded, we enforce
    // visibility via permission checks.

    // Feature Management visibility (main + sub-permissions)
    const canSeeFeatureManagement =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "feature_management.view",
            "feature_management.admin",
            "feature_management.features.view",
            "feature_management.features.manage",
            "feature_management.feature_toggles.view",
            "feature_management.feature_toggles.manage",
        ]);

    // Access Management should be visible when user has any access-management
    // permission (view/admin or any sub-permission like users/roles/permissions/audit_logs).
    const canSeeAccessManagement =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "access_management.view",
            "access_management.admin",
            "access_management.users.view",
            "access_management.users.manage",
            "access_management.roles.view",
            "access_management.roles.manage",
            "access_management.permissions.view",
            "access_management.permissions.manage",
            "access_management.audit_logs.view",
        ]);

    // Shop Services should only be visible when user has shop view/admin or any shop-specific permissions
    const canSeeShopServices =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "shop.view",
            "shop.admin",
            "shop.seller.view",
            "shop.seller.manage",
            "shop.product.view",
            "shop.product.manage",
            "shop.order.view",
            "shop.order.manage",
        ]);

    // Promos should only be visible when user has promo view/admin, campaigns, or audit-logs permissions
    const canSeePromos =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "promo.view",
            "promo.admin",
            "promo.campaigns.view",
            "promo.campaigns.manage",
            "promo.audit_logs.view",
        ]);

    // Academy Enrolments should be visible when user has any academy-management permission
    const canSeeAcademyEnrolments =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "academy_management.admin",
            "academy_management.view",
            "academy_management.enrollments.view",
            "academy_management.enrollments.manage",
            "academy_management.trials.manage",
        ]);

    // Ads should only be visible when user has ads view/admin/manage permissions
    const canSeeAds =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "ads.view",
            "ads.admin",
            "ads.manage",
        ]);

    // Tournaments menu should be visible whenever the user has ANY tournament-
    // related permission (admin, view, or specific sub-permissions).
    const canSeeTournamentsMenu =
        !permissionsLoaded ||
        hasAnyPermission(userData, [
            "tournament.admin",
            "tournament.manage",
            "tournament.view",
            "tournament.read",
            "tournament.registrations.view",
            "tournament.registrations.manage",
            "tournament.dashboard.view",
            "tournament.registration_details.manage",
            "club.read",
            "club.manage",
            "tournament.players.view",
            "tournament.players.manage",
            "tournament.payments.view",
            "tournament.tournaments.view",
            "tournament.tournaments.manage",
            "tournament.fixtures.manage",
            "sports_management.view",
            "sports_management.manage",
        ]);

    function updateIconSidebar(e: any) {
        if (e && e.target && e.target.getAttribute("sub-items")) {
            const ul: any = document.getElementById("two-column-menu");
            const iconItems: any = ul.querySelectorAll(".nav-icon.active");
            let activeIconItems = [...iconItems];
            activeIconItems.forEach((item) => {
                item.classList.remove("active");
                var id: any = item.getAttribute("sub-items");
                var menusId = document.getElementById(id);
                if (menusId){
                    (menusId.parentElement as HTMLElement).classList.remove("show");
                }
            });
            e.target.classList.add("active");
        }
    }
    useEffect(() => {
        document.body.classList.remove('twocolumn-panel');

        if (iscurrentState !== 'Dashboard') {
            setIsDashboard(false);
        }
        if (iscurrentState !== 'Ecommerce') {
            setIsEcommerce(false);
        }
        if (iscurrentState !== 'Learning') {
            setIsLearning(false);
        }
        if (iscurrentState !== 'Invoice') {
            setIsInvoice(false);
        }
        if (iscurrentState !== 'Support Ticket') {
            setIsSupportTicket(false);
        }
        if (iscurrentState !== 'Real Estate') {
            setIsRealEstate(false);
        }
        if (iscurrentState !== 'Authentication') {
            setIsAuthentication(false);
        }
        if (iscurrentState !== 'Pages') {
            setIsPages(false);
        }
        if (iscurrentState !== 'Bootstrap UI') {
            setIsBootstrapUi(false);
        }
        if (iscurrentState !== 'AdvanceUi') {
            setIsAdvanceUi(false);
        }
        if (iscurrentState !== 'Custom UI') {
            setIsCustomUi(false);
        }
        if (iscurrentState !== 'Widgets') {
            setIsWidgets(false);
        }
        if (iscurrentState !== 'Forms') {
            setIsForms(false);
        }
        if (iscurrentState !== 'Tables') {
            setIsTables(false);
        }
        if (iscurrentState !== 'Charts') {
            setIsCharts(false);
        }
        if (iscurrentState !== 'Icons') {
            setIsIcons(false);
        }
        if (iscurrentState !== 'Maps') {
            setIsMaps(false);
        }
        if (iscurrentState !== 'My Club') {
            setIsMyClub(false);
        }

        if (iscurrentState !== 'Orders') {
            setIsOrder(false);
        }
        // if (iscurrentState !== 'Sellers') {
        //     setIsSellers(false);
        // }
        if (iscurrentState !== 'Shipping') {
            setIsShipping(false);
        }
        if (iscurrentState !== 'Localization') {
            setIsLocalization(false);
        }
        if (iscurrentState !== 'Auth') {
            setIsAuth(false);
        }
    }, [
        iscurrentState,
        isDashboard,
        isLearning,
        isRealEstate,
        isEcommerce,
        isSupportTicket,
        isOrder,
        isInvoice,
        isShipping,
        isLocalization,
        isAuth,
        isMultiLevel,
        isAuthentication,
        isPages,
        isBootstrapUi,
        isAdvanceUi,
        isWidgets,
        isForms,
        isTables,
        isCharts,
        isIcons,
        isMaps,
        isMyClub
    ]);

    const allMenuItems: any = [
        {
            label: "Menu",
            isHeader: true,
        },
        // Only show main dashboard for club-manager (no My Club menu for now;
        // My Club will be wired to dedicated permissions later)
        ...(isClubManager(userData) ? [{
            id: "dashboard",
            label: "Dashboard",
            icon: "ph-gauge",
            link: "/dashboard",
        }] : []),
        // Tournaments menu (simple for club managers, complex otherwise) when
        // user has tournaments module access.
        ...(canSeeTournamentsMenu ? [getTournamentMenu(userData)] : []),
        {
            id: "app-website",
            label: "App & Website",
            icon: "ph-globe",
            link: "/app-website",
            subItems: [
                {
                    id: "weeklyreports",
                    label: "Weekly Reports",
                    icon: "ph-chart-line",
                    link: "/WeeklyReports"
                },
                {
                    id: "bannermanagement",
                    label: "Banner Management",
                    icon: "ph-image",
                    link: "/banner-management"
                },
                {
                    id: "newsarticles",
                    label: "News Articles",
                    icon: "ph-newspaper",
                    link: "/news-articles"
                },
                {
                    id: "blogdetails",
                    label: "Blog Details",
                    icon: "ph-book-open",
                    link: "/blog-details",
                    subItems: [
                        { id: "categories", label: "Categories", icon: "ph-tag", link: "/blog-details/categories" },
                        { id: "blogs", label: "Blogs", icon: "ph-article", link: "/blog-details/blogs" }
                    ]
                }
            ]
        },
        ...(canSeeShopServices
            ? [
                  {
                      id: "shopservices",
                      label: "Shop Services",
                      icon: "ph-shopping-bag",
                      link: "/shop-services/sellers",
                      subItems: [
                          { id: "sellers", label: "Sellers", link: "/shop-services/sellers" },
                          { id: "products", label: "Products", link: "/shop-services/products" },
                          { id: "orders", label: "Orders", link: "/shop-services/orders" },
                      ],
                  },
              ]
            : []),
        ...(canSeePromos
            ? [
                  {
                      id: "promos",
                      label: "Promos",
                      icon: "ph-tag",
                      link: "/promos/campaigns",
                      subItems: [
                          { id: "all-promos", label: "All Promos", link: "/promos/promos" },
                          { id: "campaigns", label: "Campaigns", link: "/promos/campaigns" },
                         { id: "audit-logs", label: "Audit Logs", link: "/promos/audit-logs" },
                      ],
                  },
              ]
            : []),

        ...(canSeeAds
            ? [
                  {
                      id: "ads",
                      label: "Ads",
                      icon: "ph-megaphone",
                      link: "/ads/overview",
                      subItems: [
                          { id: "ads-overview", label: "Overview", icon: "ph-house", link: "/ads/overview" },
                          { id: "ads-advertisers", label: "Advertisers", icon: "ph-user", link: "/ads/advertisers" },
                          { id: "ads-campaigns", label: "Campaigns", icon: "ph-list", link: "/ads/campaigns" },
                          { id: "ads-creatives", label: "Ads", icon: "ph-megaphone", link: "/ads/creatives" },
                          { id: "ads-placements", label: "Placements", icon: "ph-map-pin", link: "/ads/placements" },
                          { id: "ads-reports", label: "Reports", icon: "ph-chart-line", link: "/ads/reports" },
                          { id: "ads-billing", label: "Billing", icon: "ph-credit-card", link: "/ads/billing" },
                          { id: "ads-audit-logs", label: "Audit Logs", icon: "ph-list-checks", link: "/ads/audit-logs" },
                      ],
                  },
              ]
            : []),

        {
            id: "community",
            label: "Community",
            icon: "ph-users-three",
            link: "/community"
        },
        ...(canSeeFeatureManagement
            ? [
                  {
                      id: "featuremanagement",
                      label: "Feature Management",
                      icon: "ph-toggle-left",
                      link: "/feature-management",
                      subItems: [
                          {
                              id: "features",
                              label: "Feature",
                              icon: "ph-list",
                              link: "/feature-management/features",
                          },
                          {
                              id: "featuretoggles",
                              label: "Feature Toggle",
                              icon: "ph-toggle-left",
                              link: "/feature-management/feature-toggles",
                          },
                      ],
                  },
              ]
            : []),
        ...(canSeeAccessManagement
            ? [
                  {
                      id: "accessmanagement",
                      label: "Access Management",
                      icon: "ph-shield-check",
                      link: "/access-management/users",
                      subItems: [
                          {
                              id: "users",
                              label: "Users",
                              icon: "ph-users",
                              link: "/access-management/users",
                          },
                          {
                              id: "roles",
                              label: "Roles",
                              icon: "ph-user-circle",
                              link: "/access-management/roles",
                          },
                          {
                              id: "permissions",
                              label: "Permissions",
                              icon: "ph-key",
                              link: "/access-management/permissions",
                          },
                          {
                              id: "auditlogs",
                              label: "Audit Logs",
                              icon: "ph-list-checks",
                              link: "/audit-logs",
                          },
                      ],
                  },
              ]
            : []),
        ...(canSeeAcademyEnrolments
            ? [
                  {
                      id: "academymanagement",
                      label: "Academy Management",
                      icon: "ph-graduation-cap",
                      link: "/academy-enrolments",
                      subItems: [
                          {
                              id: "academyenrolments",
                              label: "Enrollments",
                              icon: "ph-clipboard-text",
                              link: "/academy-enrolments",
                          },
                          {
                              id: "academytrialadd",
                              label: "Trials",
                              icon: "ph-flag",
                              link: "/academy-trials/add",
                          },
                          {
                              id: "academycourseassignments",
                              label: "Course Assignments",
                              icon: "ph-files",
                              link: "/academy-course-assignments",
                          },
                      ],
                  },
              ]
            : []),
        {
            id: "playervault",
            label: "Player Vault",
            icon: "ph-vault",
            link: "/player-vault/claim-requests",
            subItems: [
                {
                    id: "playervaultclaimrequests",
                    label: "Claim Requests",
                    icon: "ph-hand-waving",
                    link: "/player-vault/claim-requests",
                },
            ],
        },
    ];

    // After permissions are loaded, apply fine-grained module filtering to hide
    // modules the user is not allowed to access. Before that, render the full
    // menu structure so active-state calculation works correctly on refresh,
    // but keep it visually hidden to avoid a flash of all items.
    const menuItems = permissionsLoaded
        ? filterMenuItems(allMenuItems, userData)
        : allMenuItems;

    return (
        <div style={{ visibility: permissionsLoaded ? "visible" : "hidden" }}>
            {menuItems}
        </div>
    );
};

export default Navdata;
